'use client';

import { useEffect, useMemo, useState } from 'react';

import { Text, Tooltip } from '@mantine/core';

import myFetch from '@utils/myFetch';

import classes from './AppVersions.module.css';

const FETCHED_KEY = 'appVersions:fetched';
const DATA_KEY = 'appVersions:data';
const ENVIRONMENT_LABELS = {
    sandbox: 'Sandbox',
    preprod: 'Preprod',
    'prod-ns': 'Prod NS',
    prod: 'Prod',
};

function normalizeVersions(payload) {
    const versions = Array.isArray(payload?.versions) ? payload.versions : [];

    return versions.filter((version) => (
        version
        && typeof version.app === 'string'
        && typeof version.environment === 'string'
        && typeof version.version === 'string'
    ));
}

function readCachedVersions() {
    try {
        const raw = sessionStorage.getItem(DATA_KEY);
        if (!raw) {
            return [];
        }

        return normalizeVersions(JSON.parse(raw));
    } catch {
        sessionStorage.removeItem(DATA_KEY);
        return [];
    }
}

function writeCachedVersions(versions) {
    try {
        sessionStorage.setItem(FETCHED_KEY, 'true');
        sessionStorage.setItem(DATA_KEY, JSON.stringify({ versions }));
    } catch {
        // Ignore storage quota errors; versions are optional UI metadata.
    }
}

export default function AppVersions() {
    const [versions, setVersions] = useState([]);

    useEffect(() => {
        const loadVersions = async () => {
            if (sessionStorage.getItem(FETCHED_KEY) === 'true') {
                setVersions(readCachedVersions());
                return;
            }

            try {
                const payload = await myFetch.get('app-versions');
                const nextVersions = normalizeVersions(payload);
                writeCachedVersions(nextVersions);
                setVersions(nextVersions);
            } catch {
                writeCachedVersions([]);
                setVersions([]);
            }
        };

        void loadVersions();
    }, []);

    const groupedVersions = useMemo(() => {
        return versions.reduce((groups, version) => {
            const current = groups.get(version.app) || [];
            current.push(version);
            groups.set(version.app, current);

            return groups;
        }, new Map());
    }, [versions]);

    const tooltipLabel = useMemo(() => {
        return [...groupedVersions.entries()]
            .map(([app, appVersions]) => {
                const details = appVersions
                    .map((version) => `${ENVIRONMENT_LABELS[version.environment] || version.environment}: ${version.version}`)
                    .join(', ');

                return `${app} (${details})`;
            })
            .join('\n');
    }, [groupedVersions]);

    if (!versions.length) {
        return null;
    }

    return (
        <>
            <Text>&bull;</Text>
            <Tooltip
                label={(
                    <span className={classes.tooltip}>
                        {tooltipLabel}
                    </span>
                )}
                multiline
                withArrow
            >
                <Text className={classes.summary}>
                    Versions deployees
                </Text>
            </Tooltip>
        </>
    );
}
